import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [locations, setLocations] = useState([]);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website_url: '' // Honeypot
  });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/locations')
      .then(res => res.json())
      .then(data => setLocations(data))
      .catch(err => console.error('Error fetching locations:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          website_url: ''
        });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="pt-32 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-black text-zinc-900 mb-4 uppercase tracking-tight">
            Kontakt Os
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Har du spørgsmål til vores materialer, levering eller priser? Vi sidder klar til at hjælpe dig.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-zinc-50 p-8 md:p-12 shadow-sm border border-zinc-100">
            <h2 className="text-2xl font-bold text-zinc-900 mb-8 uppercase tracking-wide">Send en besked</h2>
            
            {status === 'success' ? (
              <div className="bg-green-50 border border-green-200 p-6 flex items-start gap-4">
                <CheckCircle2 className="text-green-600 shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-green-900">Tak for din besked!</h3>
                  <p className="text-green-700">Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="mt-4 text-green-800 font-bold underline underline-offset-4 hover:text-green-900"
                  >
                    Send en ny besked
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot - hidden from humans */}
                <div className="sr-only absolute opacity-0 pointer-events-none">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Navn</label>
                    <input
                      required
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-zinc-200 focus:border-[#a3844e] focus:ring-0 transition-colors outline-none"
                      placeholder="Dit navn"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">E-mail</label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-zinc-200 focus:border-[#a3844e] focus:ring-0 transition-colors outline-none"
                      placeholder="din@email.dk"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-zinc-200 focus:border-[#a3844e] focus:ring-0 transition-colors outline-none"
                      placeholder="+45 00 00 00 00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Emne</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-zinc-200 focus:border-[#a3844e] focus:ring-0 transition-colors outline-none"
                    >
                      <option value="">Vælg emne</option>
                      <option value="Tilbud">Forespørgsel på tilbud</option>
                      <option value="Levering">Spørgsmål til levering</option>
                      <option value="Materialer">Spørgsmål til materialer</option>
                      <option value="Andet">Andet</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Besked</label>
                  <textarea
                    required
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-zinc-200 focus:border-[#a3844e] focus:ring-0 transition-colors outline-none resize-none"
                    placeholder="Hvordan kan vi hjælpe dig?"
                  ></textarea>
                </div>

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 p-4 flex items-center gap-3 text-red-700">
                    <AlertCircle size={20} />
                    <p>Der opstod en fejl. Prøv venligst igen senere.</p>
                  </div>
                )}

                <button
                  disabled={status === 'loading'}
                  type="submit"
                  className="w-full bg-zinc-900 text-white py-4 px-8 font-bold uppercase tracking-widest hover:bg-[#a3844e] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {status === 'loading' ? 'Sender...' : (
                    <>
                      Send besked
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info & Locations */}
          <div className="space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 mb-8 uppercase tracking-wide">Vores Afdelinger</h2>
              <div className="grid grid-cols-1 gap-8">
                {locations.map((loc) => (
                  <div key={loc.id} className="p-6 border border-zinc-100 bg-zinc-50/50">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-zinc-900 mb-3 uppercase">{loc.name.da}</h3>
                      <div className="space-y-2 text-zinc-600">
                        <p className="flex items-start gap-3">
                          <MapPin size={18} className="text-black shrink-0 mt-1" />
                          <span>{loc.address}, {loc.zip} {loc.city}</span>
                        </p>
                        <p className="flex items-center gap-3">
                          <Phone size={18} className="text-black shrink-0" />
                          <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="hover:text-[#a3844e] transition-colors">{loc.phone}</a>
                        </p>
                        <div className="flex items-start gap-3 pt-2">
                          <Clock size={18} className="text-black shrink-0 mt-1" />
                          <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: loc.opening_hours.da }} />
                        </div>
                      </div>
                      <a 
                        href={loc.map_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-4 text-sm font-bold text-zinc-900 hover:text-[#a3844e] transition-colors uppercase tracking-wider border-b-2 border-zinc-200 hover:border-[#a3844e]"
                      >
                        Find vej på kort
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-zinc-900 text-white p-8">
              <h3 className="text-xl font-bold mb-4 uppercase tracking-wide">Hurtig kontakt</h3>
              <p className="text-zinc-400 mb-6">Har du brug for et hurtigt svar? Ring til os direkte i vores åbningstid.</p>
              <a 
                href="tel:+4520181271" 
                className="text-3xl font-black text-[#a3844e] hover:text-white transition-colors"
              >
                +45 20 18 12 71
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
